import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetBlueprintFamilyTrendsBatchQuery } from '../../../../api/blueprintFamilies/trendsBatch'
import { BlueprintFamilyTrendsPoint, BlueprintFamilyTrendsWindowDays } from '../../../../api/core'
import { TrendsChartSeriesItem, TrendsLineChartCard } from '../TrendsLineChartCard'
import { CatalogueMetricKey } from '../helpers/catalogueUtils'
import { selectedPeriodFor } from '../helpers/trendsSelectedPeriod'
import { MAX_TRENDS_SERIES, pickTrendsSeriesForMetric } from '../helpers/trendsSeries'

interface BlueprintTrendsChartProps {
  items: TrendsChartSeriesItem[]
  // Which series to plot. Owned by the caller (the page's metric lens) so the
  // list beside the chart and the chart never disagree on what is shown.
  metric: CatalogueMetricKey
  windowDays?: BlueprintFamilyTrendsWindowDays
  conventionalSubgenreIds?: string[]
  isLoading?: boolean
  // Rendered between the header and the canvas. Exists so a caller plotting
  // these blueprint series under a coarser grouping can say so where the lines
  // are, not somewhere else on the page.
  notice?: React.ReactNode
  // Right-aligned header slot for the control that changes the plotted series.
  headerControls?: React.ReactNode
  // Rendered under the canvas, full card width.
  footer?: React.ReactNode
  // Overrides the default per-metric title (the detail pages name the level).
  title?: string
  // Hides the "Top N blueprint families in view" caption.
  hideSubtitle?: boolean
  // Full-width row above the title (metric lens tabs).
  tabs?: React.ReactNode
  // Render without the Card shell, for embedding inside another card.
  embedded?: boolean
  // Line color per item id; items without one fall back to their position.
  colorById?: Record<string, string>
}

const TITLE_KEYS: Record<CatalogueMetricKey, string> = {
  adoption: 'market-analytics:blueprint_trends_adoption_title',
  density: 'market-analytics:blueprint_trends_density_title',
  activeEvents: 'market-analytics:blueprint_trends_active_events_title',
}

const ARIA_KEYS: Record<CatalogueMetricKey, string> = {
  adoption: 'market-analytics:blueprint_trends_adoption_aria',
  density: 'market-analytics:blueprint_trends_density_aria',
  activeEvents: 'market-analytics:blueprint_trends_active_events_aria',
}

/**
 * One line per blueprint family, fed by the batched `trends/batch` endpoint.
 * Presentation (monthly bucketing, legend, description) is `TrendsLineChartCard`.
 */
export const BlueprintTrendsChart = React.memo(function BlueprintTrendsChart({
  items,
  metric,
  windowDays,
  conventionalSubgenreIds,
  isLoading = false,
  notice,
  headerControls,
  footer,
  title,
  hideSubtitle = false,
  tabs,
  embedded,
  colorById,
}: BlueprintTrendsChartProps) {
  const { t } = useTranslation()

  // Slice to the endpoint max before requesting; everything downstream works off this list.
  const slicedItems = useMemo(() => items.slice(0, MAX_TRENDS_SERIES), [items])
  const slicedIds = useMemo(() => slicedItems.map((item) => item.id), [slicedItems])

  // Display-only: the response covers the selected period, and only its months are
  // plotted should it carry more.
  const selectedPeriod = useMemo(() => selectedPeriodFor(windowDays), [windowDays])

  // `currentData`, not `data`: these series are scoped to a segment and a window, and
  // `data` holds the previous arguments' response until the new one lands. Family ids
  // survive a segment change, so the old series would be plotted under the new chip —
  // the market's figures for a segment the user is no longer looking at. The card is in
  // its loading state throughout that gap (see `isLoading` below), so dropping to
  // undefined shows a skeleton rather than an empty chart.
  const { currentData, isFetching, isError, refetch } = useGetBlueprintFamilyTrendsBatchQuery(
    { ids: slicedIds, windowDays, conventionalSubgenreIds },
    { skip: slicedIds.length === 0 }
  )

  const seriesById = useMemo(() => {
    const byId: Record<string, BlueprintFamilyTrendsPoint[]> = {}
    ;(currentData ?? []).forEach((item) => {
      byId[item.id] = pickTrendsSeriesForMetric(item, metric)
    })
    return byId
  }, [currentData, metric])

  return (
    <TrendsLineChartCard
      title={title ?? t(TITLE_KEYS[metric])}
      ariaLabel={t(ARIA_KEYS[metric], { count: slicedItems.length })}
      nameColumnHeader={t('market-analytics:market_level_blueprint_name')}
      items={slicedItems}
      seriesById={seriesById}
      metric={metric}
      selectedPeriod={selectedPeriod ?? undefined}
      isLoading={isLoading || (slicedIds.length > 0 && isFetching)}
      isError={slicedIds.length > 0 && isError && !currentData}
      onRetry={refetch}
      subtitle={hideSubtitle ? undefined : t('market-analytics:catalogue_chart_subtitle', { count: slicedItems.length })}
      notice={notice}
      headerControls={headerControls}
      footer={footer}
      tabs={tabs}
      embedded={embedded}
      colorById={colorById}
    />
  )
})

BlueprintTrendsChart.displayName = 'BlueprintTrendsChart'
